import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import ParticipantsList from '../components/ParticipantsList';
import { DotLoader } from 'react-spinners';
import { 
  getCalendar, 
  cancelSeance, 
  reactivateSeance, 
  removeSeanceFromCalendar 
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  MapPin, 
  X, 
  RotateCcw, 
  Trash2,
  CheckCircle,
  XCircle,
  List
} from 'lucide-react';

function MyCalendar() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [seances, setSeances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedSeance, setSelectedSeance] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' ou 'list'

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCalendar();
  }, [isAuthenticated, navigate]);

  const fetchCalendar = async () => {
    try {
      const data = await getCalendar();
      setSeances(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement du calendrier:', error);
      setLoading(false);
    }
  };

  const handleCancelSeance = async (seanceId) => {
    try {
      await cancelSeance(seanceId);
      fetchCalendar();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'annulation');
    }
  };

  const handleReactivateSeance = async (seanceId) => {
    try {
      await reactivateSeance(seanceId);
      fetchCalendar();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la réactivation');
    }
  };

  const handleRemoveSeance = async (seanceId) => {
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer cette séance de votre calendrier ?');
    if (!confirmed) return;

    try {
      await removeSeanceFromCalendar(seanceId);
      fetchCalendar();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatPace = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTypeSeanceColor = (typeSeance) => {
    const colors = {
      'Endurance Fondamentale': 'bg-green-500',
      'Récupération': 'bg-blue-500',
      'Seuil/Tempo': 'bg-yellow-500',
      'Fractionné Court': 'bg-red-500',
      'Fractionné Long': 'bg-orange-500',
      'Sortie Longue': 'bg-purple-500',
      'Autre': 'bg-gray-500'
    };
    return colors[typeSeance] || 'bg-slate-600';
  };

  const moisFrancais = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const joursFrancais = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const joursFrancaisComplet = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getSeancesForDate = (date) => {
    if (!date) return [];
    return seances.filter(seance => {
      const seanceDate = new Date(seance.date);
      return seanceDate.getDate() === date.getDate() &&
             seanceDate.getMonth() === date.getMonth() &&
             seanceDate.getFullYear() === date.getFullYear();
    });
  };

  const getSeancesForMonth = () => {
    return seances.filter(seance => {
      const seanceDate = new Date(seance.date);
      return seanceDate.getMonth() === currentDate.getMonth() &&
             seanceDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const days = getDaysInMonth(currentDate);

  const seancesConfirmees = seances.filter(s => s.statut === 'confirmee').length;
  const seancesAnnulees = seances.filter(s => s.statut === 'annulee').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center">
            <CalendarIcon className="mr-2 sm:mr-3 text-indigo-500" size={28} />
            Mon Calendrier
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-md p-4 sm:p-5 border-l-4 border-l-indigo-500">
            <ul className="space-y-2">
              <li className="flex items-start text-sm sm:text-base text-gray-300">
                <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Gérez vos séances d'entraînement en les ajoutant à partir du calendrier d'un club</span>
              </li>
              <li className="flex items-start text-sm sm:text-base text-gray-300">
                <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Cliquez sur les séances pour avoir plus de détails</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Total des séances</p>
                <p className="text-2xl sm:text-3xl font-bold text-indigo-500">{seances.length}</p>
              </div>
              <CalendarIcon size={40} className="text-slate-700 hidden sm:block" />
              <CalendarIcon size={32} className="text-slate-700 sm:hidden" />
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Séances confirmées</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-500">{seancesConfirmees}</p>
              </div>
              <CheckCircle size={40} className="text-green-900/30 hidden sm:block" />
              <CheckCircle size={32} className="text-green-900/30 sm:hidden" />
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 sm:col-span-2 md:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Séances annulées</p>
                <p className="text-2xl sm:text-3xl font-bold text-red-500">{seancesAnnulees}</p>
              </div>
              <XCircle size={40} className="text-red-900/30 hidden sm:block" />
              <XCircle size={32} className="text-red-900/30 sm:hidden" />
            </div>
          </div>
        </div>

        {/* Calendrier */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-6">
          {/* Navigation et Toggle View */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-4 sm:mb-6 gap-3">
            <div className="flex items-center space-x-3 w-full sm:w-auto justify-center">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-slate-800 text-gray-300 hover:text-white rounded-full transition"
                aria-label="Mois précédent"
              >
                <ChevronLeft size={24} />
              </button>
              <h3 className="text-lg sm:text-xl font-bold text-white min-w-[180px] sm:min-w-[220px] text-center">
                {moisFrancais[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-slate-800 text-gray-300 hover:text-white rounded-full transition"
                aria-label="Mois suivant"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Toggle view mode */}
            <div className="flex md:hidden bg-slate-800 border border-slate-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                  viewMode === 'calendar' 
                    ? 'bg-indigo-500 text-white shadow-sm' 
                    : 'text-gray-400'
                }`}
              >
                <CalendarIcon size={16} />
                <span>Grille</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                  viewMode === 'list' 
                    ? 'bg-indigo-500 text-white shadow-sm' 
                    : 'text-gray-400'
                }`}
              >
                <List size={16} />
                <span>Liste</span>
              </button>
            </div>
          </div>

          {/* Vue Liste */}
          {viewMode === 'list' && (
            <div className="md:hidden space-y-3">
              {getSeancesForMonth().length === 0 ? (
                <p className="text-center text-gray-400 py-8">Aucune séance ce mois-ci</p>
              ) : (
                getSeancesForMonth().map((seance, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setSelectedSeance(seance);
                      setShowModal(true);
                    }}
                    className={`${getTypeSeanceColor(seance.typeSeance)} ${
                      seance.statut === 'annulee' ? 'opacity-60' : ''
                    } p-4 rounded-lg text-white cursor-pointer hover:opacity-90 transition shadow-md`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon size={16} />
                        <span className="font-semibold">
                          {new Date(seance.date).toLocaleDateString('fr-FR', { 
                            weekday: 'short', 
                            day: 'numeric', 
                            month: 'short' 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span className="font-semibold text-sm">{seance.heure}</span>
                      </div>
                    </div>
                    <div className="font-bold text-lg mb-1">{seance.typeSeance}</div>
                    {seance.clubId && (
                      <div className="text-sm opacity-90 flex items-center space-x-1">
                        <MapPin size={12} />
                        <span>{seance.clubId.nom}</span>
                      </div>
                    )}
                    {seance.statut === 'annulee' && (
                      <div className="text-xs font-bold mt-2 bg-white bg-opacity-20 inline-block px-2 py-1 rounded">
                        ANNULÉE
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Vue Calendrier */}
          {viewMode === 'calendar' && (
            <>
              {/* Jours de la semaine */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-4">
                {joursFrancais.map((jour, index) => (
                  <div key={index} className="text-center font-semibold text-gray-400 text-xs sm:text-sm py-1 sm:py-2">
                    {jour}
                  </div>
                ))}
              </div>

              {/* Grille du calendrier */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {days.map((date, index) => {
                  const seancesForDay = date ? getSeancesForDate(date) : [];
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[70px] sm:min-h-[100px] md:min-h-32 p-1 sm:p-2 border rounded-lg ${
                        !date ? 'bg-slate-950 border-slate-800' :
                        isToday(date) ? 'bg-indigo-900/30 border-indigo-500 border-2' :
                        'bg-slate-800 border-slate-700 hover:border-indigo-500'
                      } transition`}
                    >
                      {date && (
                        <>
                          <div className="text-xs sm:text-sm font-semibold text-gray-300 mb-1 text-center sm:text-left">
                            {date.getDate()}
                          </div>
                          
                          {seancesForDay.length > 0 && (
                            <div className="space-y-1">
                              {seancesForDay.map((seance, idx) => (
                                <div key={idx}>
                                  <div
                                    onClick={() => {
                                      setSelectedSeance(seance);
                                      setShowModal(true);
                                    }}
                                    className={`${getTypeSeanceColor(seance.typeSeance)} ${
                                      seance.statut === 'annulee' ? 'opacity-50' : ''
                                    } text-white text-xs p-1 sm:p-2 rounded cursor-pointer hover:opacity-80 transition`}
                                  >
                                    {/* Version desktop */}
                                    <div className="hidden sm:block">
                                      <div className="flex items-center justify-between mb-1">
                                        <Clock size={10} />
                                        <span className="font-semibold">{seance.heure}</span>
                                      </div>
                                      <div className="font-semibold truncate">{seance.typeSeance}</div>
                                      {seance.clubId && (
                                        <div className="text-xs opacity-90 truncate mt-1">
                                          {seance.clubId.nom}
                                        </div>
                                      )}
                                      {seance.statut === 'annulee' && (
                                        <div className="text-xs font-bold mt-1">ANNULÉE</div>
                                      )}
                                    </div>

                                    {/* Version mobile */}
                                    <div className="sm:hidden flex items-center justify-center">
                                      <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Modal détails séance */}
        {showModal && selectedSeance && (
          <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-start mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Détails de la séance</h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-white text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {selectedSeance.clubId && (
                    <div className="bg-slate-800 border border-slate-700 p-3 sm:p-4 rounded-lg">
                      <p className="text-xs sm:text-sm text-gray-400">Club</p>
                      <p className="text-base sm:text-lg font-semibold text-white">{selectedSeance.clubId.nom}</p>
                      {selectedSeance.clubId.ville && (
                        <div className="flex items-center text-gray-300 mt-1">
                          <MapPin size={14} className="mr-1 text-indigo-500" />
                          <span className="text-sm">{selectedSeance.clubId.ville}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Date</p>
                    <p className="text-base sm:text-lg font-semibold text-white">
                      {new Date(selectedSeance.date).toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Heure</p>
                    <p className="text-base sm:text-lg font-semibold text-white">{selectedSeance.heure}</p>
                  </div>

                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Type de séance</p>
                    <span className={`inline-block px-3 sm:px-4 py-2 rounded-full text-white text-sm sm:text-base font-semibold ${getTypeSeanceColor(selectedSeance.typeSeance)}`}>
                      {selectedSeance.typeSeance}
                    </span>
                  </div>

                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Statut</p>
                    <span className={`inline-block px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base font-semibold border ${
                      selectedSeance.statut === 'confirmee' 
                        ? 'bg-green-900/20 border-green-500 text-green-400' 
                        : 'bg-red-900/20 border-red-500 text-red-400'
                    }`}>
                      {selectedSeance.statut === 'confirmee' ? 'Confirmée' : 'Annulée'}
                    </span>
                  </div>

                  {selectedSeance.distance && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-400">Distance</p>
                      <p className="text-base sm:text-lg font-semibold text-white">{selectedSeance.distance}</p>
                    </div>
                  )}

                  {selectedSeance.paceMin && selectedSeance.paceMax && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-400">Allure cible</p>
                      <p className="text-base sm:text-lg font-semibold text-white">
                        {formatPace(selectedSeance.paceMin)} - {formatPace(selectedSeance.paceMax)} /km
                      </p>
                    </div>
                  )}

                  {selectedSeance.description && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-400">Description</p>
                      <p className="text-sm sm:text-base text-gray-300">{selectedSeance.description}</p>
                    </div>
                  )}

                  {selectedSeance.lieu && (
                    <div className="flex items-center text-gray-300 text-sm">
                      <MapPin size={16} className="mr-2 text-indigo-500" />
                      <span>{selectedSeance.lieu}</span>
                    </div>
                  )}

                  {/* Participants */}
                  {selectedSeance.clubId && (
                    <div className="pt-4 border-t border-slate-800">
                      <ParticipantsList
                        clubId={selectedSeance.clubId._id}
                        seanceIndex={selectedSeance.seanceId}
                        date={selectedSeance.date}
                        seance={selectedSeance}
                      />
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
                  {selectedSeance.statut === 'confirmee' ? (
                    <button
                      onClick={() => handleCancelSeance(selectedSeance._id)}
                      className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg active:scale-95 transition-all font-semibold text-sm sm:text-base shadow-lg"
                    >
                      <X size={18} className="flex-shrink-0" />
                      <span className="truncate">Retirer de mon calendrier</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivateSeance(selectedSeance._id)}
                      className="w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg active:scale-95 transition-all font-semibold text-sm sm:text-base shadow-lg"
                    >
                      <RotateCcw size={18} className="flex-shrink-0" />
                      <span>Réactiver</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleRemoveSeance(selectedSeance._id)}
                    className="w-full flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg active:scale-95 transition-all font-semibold text-sm sm:text-base shadow-lg"
                  >
                    <Trash2 size={18} className="flex-shrink-0" />
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MyCalendar;